import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Lock, Bell, BellOff, RefreshCw, Trash2, LogOut, Home,
  BarChart3, Clock, Users, Music, Truck, Store, Building2,
  Megaphone, Video, Sprout, FlaskConical, Disc, Calendar,
  Mail, Image, Star, Settings, DollarSign, Cannabis,
  Activity, Palette, ChevronDown, Menu, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AdminProvider, useAdmin,
  AdminOverviewTab, AdminHomepageTab, AdminUsersTab,
  AdminRegistrationsTab, AdminArtistsTab, AdminFoodTrucksTab, AdminVendorsTab,
  AdminBusinessesTab, AdminBandContentTab, AdminEventsTab,
  AdminFeaturesTab, AdminPromotersTab, AdminCreatorsTab,
  AdminGrowersTab, AdminExtractorsTab, AdminDispensariesTab, AdminMessagesTab,
  AdminAdsTab, AdminSettingsTab, AdminRevenueTab,
  AdminSiteAuditTab, AdminBrandKitTab
} from '../components/admin';

const NAV_GROUPS = [
  {
    label: 'Dashboard',
    items: [
      { key: 'overview', label: 'Overview', icon: BarChart3 },
      { key: 'revenue', label: 'Revenue', icon: DollarSign },
    ]
  },
  {
    label: 'Content',
    items: [
      { key: 'homepage', label: 'Homepage', icon: Home },
      { key: 'artists', label: 'Artists', icon: Music },
      { key: 'events', label: 'Events', icon: Calendar, pendingKey: 'events' },
      { key: 'band-content', label: 'Artist Content', icon: Disc, pendingKey: 'bandContent' },
      { key: 'ads', label: 'Ads', icon: Image },
      { key: 'features', label: 'Featured', icon: Star },
    ]
  },
  {
    label: 'Business',
    items: [
      { key: 'vendors', label: 'Vendors', icon: Store },
      { key: 'food-trucks', label: 'Food Trucks', icon: Truck },
      { key: 'businesses', label: 'Businesses', icon: Building2 },
      { key: 'dispensaries', label: 'Dispensaries', icon: Cannabis, pendingKey: 'dispensaries' },
    ]
  },
  {
    label: 'People',
    items: [
      { key: 'users', label: 'Users', icon: Users },
      { key: 'registrations', label: 'Pending', icon: Clock, pendingKey: 'registrations' },
      { key: 'promoters', label: 'Promoters', icon: Megaphone, pendingKey: 'promoters' },
      { key: 'creators', label: 'Creators', icon: Video, pendingKey: 'creators' },
      { key: 'growers', label: 'Growers', icon: Sprout, pendingKey: 'growers' },
      { key: 'extractors', label: 'Extractors', icon: FlaskConical, pendingKey: 'extractors' },
    ]
  },
  {
    label: 'Tools',
    items: [
      { key: 'site-audit', label: 'Site Audit', icon: Activity },
      { key: 'brand-kit', label: 'Brand Kit', icon: Palette },
      { key: 'messages', label: 'Messages', icon: Mail, pendingKey: 'messages' },
      { key: 'settings', label: 'Settings', icon: Settings },
    ]
  },
];

const TAB_COMPONENTS = {
  'overview': AdminOverviewTab,
  'homepage': AdminHomepageTab,
  'users': AdminUsersTab,
  'registrations': AdminRegistrationsTab,
  'artists': AdminArtistsTab,
  'food-trucks': AdminFoodTrucksTab,
  'vendors': AdminVendorsTab,
  'businesses': AdminBusinessesTab,
  'promoters': AdminPromotersTab,
  'creators': AdminCreatorsTab,
  'growers': AdminGrowersTab,
  'extractors': AdminExtractorsTab,
  'dispensaries': AdminDispensariesTab,
  'band-content': AdminBandContentTab,
  'events': AdminEventsTab,
  'messages': AdminMessagesTab,
  'ads': AdminAdsTab,
  'features': AdminFeaturesTab,
  'settings': AdminSettingsTab,
  'revenue': AdminRevenueTab,
  'site-audit': AdminSiteAuditTab,
  'brand-kit': AdminBrandKitTab,
};

const AdminDashboardInner = () => {
  const {
    isLoggedIn, password, setPassword, loginLoading, handleLogin, handleLogout,
    activeTab, setActiveTab, loading, refreshData,
    pushEnabled, pushLoading, enablePushNotifications, disablePushNotifications, testPushNotification,
    cleanupTestData, initializeDatabase,
    bandRegistrations, venueRegistrations, promoterRegistrations, contentCreatorRegistrations,
    growerRegistrations, extractorRegistrations, eventSubmissions,
    dispensaryRegistrations,
    bandSubmittedShorts, bandSubmittedEvents, contactMessages
  } = useAdmin();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Card className="bg-card border-border">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-4"><Shield size={32} className="text-primary" /></div>
              <CardTitle className="font-heading text-2xl">Admin Login</CardTitle>
              <CardDescription>Enter the admin password to access the dashboard</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label>Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter admin password" className="pl-10" required data-testid="admin-password-input" />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loginLoading} data-testid="admin-login-btn">{loginLoading ? 'Logging in...' : 'Login'}</Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Pending counts
  const pendingCounts = {
    registrations: bandRegistrations.filter(r => r.status === 'pending').length
      + venueRegistrations.filter(r => r.status === 'pending').length
      + promoterRegistrations.filter(r => r.status === 'pending').length
      + contentCreatorRegistrations.filter(r => r.status === 'pending').length
      + eventSubmissions.filter(r => r.status === 'pending').length,
    promoters: promoterRegistrations.filter(r => r.status === 'pending').length,
    creators: contentCreatorRegistrations.filter(r => r.status === 'pending').length,
    growers: growerRegistrations.filter(r => r.status === 'pending').length,
    extractors: extractorRegistrations.filter(r => r.status === 'pending').length,
    dispensaries: dispensaryRegistrations.filter(r => r.status === 'pending').length,
    bandContent: bandSubmittedShorts.filter(s => s.status === 'pending').length + bandSubmittedEvents.filter(e => e.status === 'pending').length,
    events: eventSubmissions.filter(e => e.status === 'pending').length,
    messages: contactMessages.length,
  };

  const totalPending = Object.values(pendingCounts).reduce((s, v) => s + v, 0);

  const toggleGroup = (label) => setCollapsedGroups(prev => ({ ...prev, [label]: !prev[label] }));

  const ActiveComponent = TAB_COMPONENTS[activeTab] || AdminOverviewTab;

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard">
      {/* Mobile toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-3 left-3 z-50 p-2 rounded-lg bg-card border border-border"
        data-testid="admin-mobile-menu"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar Overlay (mobile) */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black/60 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar Navigation */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen w-56 bg-card border-r border-border z-40 flex flex-col transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        {/* Logo */}
        <div className="p-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-primary shrink-0" />
            <div>
              <h1 className="text-sm font-bold">
                <span className="text-primary">TWIN</span><span className="text-secondary">TUBE</span>
                <span className="text-muted-foreground ml-1">Admin</span>
              </h1>
              <p className="text-[8px] text-gray-500">Mystery Machine Productions</p>
            </div>
          </div>
        </div>

        {/* Nav Groups */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <button
                onClick={() => toggleGroup(group.label)}
                className="flex items-center justify-between w-full px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 transition"
              >
                {group.label}
                <ChevronDown size={10} className={`transition-transform ${collapsedGroups[group.label] ? '-rotate-90' : ''}`} />
              </button>
              <AnimatePresence initial={false}>
                {!collapsedGroups[group.label] && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="overflow-hidden"
                  >
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const pending = item.pendingKey ? pendingCounts[item.pendingKey] : 0;
                      const isActive = activeTab === item.key;
                      return (
                        <button
                          key={item.key}
                          onClick={() => { setActiveTab(item.key); setSidebarOpen(false); }}
                          className={`flex items-center gap-2 w-full px-3 py-1.5 rounded-lg text-xs transition mb-0.5 ${isActive ? 'bg-purple-500/15 text-purple-400 font-semibold' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                          data-testid={`admin-nav-${item.key}`}
                        >
                          <Icon size={14} />
                          <span className="flex-1 text-left">{item.label}</span>
                          {pending > 0 && (
                            <span className="bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">{pending}</span>
                          )}
                        </button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </nav>

        {/* Footer Actions */}
        <div className="shrink-0 p-2 border-t border-border space-y-1">
          <button onClick={() => window.location.href = '/'} className="flex items-center gap-2 w-full px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white hover:bg-white/5 transition">
            <Home size={14} /> Back to Site
          </button>
          <button onClick={handleLogout} className="flex items-center gap-2 w-full px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-red-400 hover:bg-red-500/5 transition" data-testid="admin-logout-btn">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0 lg:ml-0">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-card/90 backdrop-blur border-b border-border">
          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-2 pl-10 lg:pl-0">
              <h2 className="text-sm font-semibold capitalize">{activeTab.replace(/-/g, ' ')}</h2>
              {totalPending > 0 && (
                <span className="bg-red-500 text-white text-[9px] font-bold rounded-full px-1.5 py-0.5">{totalPending} pending</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button variant={pushEnabled ? "default" : "outline"} size="sm" onClick={pushEnabled ? disablePushNotifications : enablePushNotifications} disabled={pushLoading} className="px-2 h-7 text-xs">
                {pushLoading ? <RefreshCw size={12} className="animate-spin" /> : pushEnabled ? <Bell size={12} className="text-green-400" /> : <BellOff size={12} />}
              </Button>
              <Button variant="outline" size="sm" onClick={refreshData} disabled={loading} className="px-2 h-7 text-xs">
                <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              </Button>
              <Button variant="ghost" size="sm" onClick={cleanupTestData} className="px-2 h-7 text-xs text-destructive hover:text-destructive" title="Remove test data">
                <Trash2 size={12} />
              </Button>
              <Button variant="ghost" size="sm" onClick={initializeDatabase} className="px-2 h-7 text-xs text-blue-500" title="Initialize DB">
                <RefreshCw size={12} />
              </Button>
            </div>
          </div>
        </header>

        {/* Tab Content */}
        <main className="p-4 sm:p-6">
          <ActiveComponent />
        </main>
      </div>
    </div>
  );
};

const AdminDashboard = () => (
  <AdminProvider>
    <AdminDashboardInner />
  </AdminProvider>
);

export default AdminDashboard;
